import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../lib/i18n';

type AuthFormProps = {
  mode?: 'login' | 'register' | 'reset';
};

function AuthForm({ mode = 'login' }: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (mode === 'register' && !acceptTerms) {
      toast.error('You must accept the Terms of Use to continue');
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message === 'Invalid login credentials') {
            toast.error('Invalid email or password. Please try again.');
          } else {
            toast.error('Failed to sign in. Please try again.');
          }
          return;
        }

        if (data?.user) {
          toast.success('Signed in successfully');
          navigate('/dashboard');
        }
      } else if (mode === 'register') {
        // First check if user already exists
        const { data: existingUser, error: existingUserError } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', email)
          .single();

        if (existingUserError && existingUserError.code !== 'PGRST116') {
          toast.error('An error occurred while checking existing users');
          return;
        }

        if (existingUser) {
          toast.error('An account with this email already exists');
          return;
        }
        
        const { data: { user }, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });

        if (signUpError) {
          if (signUpError.message.includes('User already registered')) {
            toast.error('An account with this email already exists');
          } else {
            toast.error('Failed to create account. Please try again.');
          }
          return;
        }

        if (!user) {
          toast.error('Failed to create account');
          return;
        }

        // Create profile
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert([{
            id: user.id,
            email: user.email,
            role: 'user',
            credits: 500,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }], {
            onConflict: 'id'
          });

        if (profileError) {
          console.error('Error creating profile:', profileError);
          toast.error('Account created but profile setup failed');
        } else {
          toast.success('Registration successful! You can now log in.');
        }
        
        setMode('login');
      } else if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) {
          toast.error('Failed to send reset instructions. Please try again.');
          return;
        }

        toast.success('Password reset instructions sent to your email');
        navigate('/login');
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast.error(error.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const setMode = (newMode: 'login' | 'register' | 'reset') => {
    navigate(newMode === 'login' ? '/login' : newMode === 'register' ? '/register' : '/reset-password');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-white hover:text-blue-200 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </button>

        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {mode === 'login' ? t('auth.login') :
             mode === 'register' ? t('auth.createAccount') :
             t('auth.resetPassword')}
          </h2>
          {mode === 'reset' && (
            <p className="mt-2 text-center text-sm text-gray-200">
              {t('auth.resetInstructions')}
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">{t('auth.email')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-white bg-blue-900/40 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder={t('auth.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            {mode !== 'reset' && (
              <div>
                <label htmlFor="password" className="sr-only">{t('auth.password')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-white bg-blue-900/40 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder={t('auth.password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                  />
                </div>
              </div>
            )}
          </div>

          {(mode === 'login' || mode === 'register') && (
            <div className="flex items-center">
              <input
                id="accept-terms"
                name="accept-terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="h-4 w-4 rounded cursor-pointer appearance-none bg-white checked:bg-cyan-500 checked:border-cyan-500 border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
              />
              <label htmlFor="accept-terms" className="ml-2 block text-sm text-gray-200">
                {t('auth.acceptTerms')}{' '}
                <Link to="/terms-of-service" className="text-blue-400 hover:text-blue-300">
                  {t('footer.terms')}
                </Link>
              </label>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-500 hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-400 transition-colors"
            >
              {loading ? t('auth.processing') :
               mode === 'login' ? t('auth.login') :
               mode === 'register' ? t('auth.register') :
               t('auth.resetPassword')}
            </button>
          </div>

          <div className="flex items-center justify-between">
            {mode === 'login' ? (
              <>
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  {t('auth.createAccount')}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('reset')}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  {t('auth.forgotPassword')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setMode('login')}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                {t('auth.backToLogin')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default AuthForm;
